import { useEffect, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import { TiDeleteOutline } from "react-icons/ti";
import { VscHistory } from "react-icons/vsc";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { handleError } from "src/Common/function";
import { setKeyword } from "src/Redux/Slices/KeywordSlice";
import AccountService from "src/Services/API/account.service";
import CustomerService from "src/Services/API/customer.service";
import KeywordService from "src/Services/API/keyword.service";
import POService from "src/Services/API/po.service";
import VendorService from "src/Services/API/vendor.service";
import WOService from "src/Services/API/wo.service";

function SearchBox() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const branch = useSelector((state) => state.Branch.branch);
  const [keywordString, setKeywordString] = useState(searchParams.get("keyword") || "");
  const [historyArray, setHistoryArray] = useState([]);
  const [suggestionArray, setSuggestionArray] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const [branch, setBranch] = useState(-1);
  // Tracks whether the current keyword change came from the user typing (so we
  // debounce-search) vs. a programmatic set such as picking a suggestion/history
  // item or syncing from the URL (which navigate immediately on their own).
  const isTypingRef = useRef(false);
  // Debounce timer for the suggestion lookups and an AbortController for the
  // in-flight request, so fast typing fires at most one batch of suggestion
  // queries (after the user pauses) and any stale request is cancelled. This
  // keeps a single user's typing from flooding the shared DB connection pool.
  const suggestionTimerRef = useRef(null);
  const suggestionAbortRef = useRef(null);

  useEffect(() => {
    const param = searchParams.get("branch");
    setBranch(param === null ? -1 : Number(param));
  }, [searchParams]);

  // Keep the input in sync when the keyword param changes from outside (e.g.
  // browser back/forward or clearing the search), without triggering a search.
  useEffect(() => {
    const urlKeyword = searchParams.get("keyword") || "";
    isTypingRef.current = false;
    setKeywordString(urlKeyword);
  }, [searchParams]);

  // Debounced search: 400ms after the user stops typing, push the keyword into
  // the URL so the listing pages (WO/PO/Recommendations) re-fetch automatically.
  useEffect(() => {
    if (!isTypingRef.current) return;

    const handle = setTimeout(() => {
      const term = keywordString.trim();
      navigate(term.length > 0 ? appendQueryParam("keyword", term) : removeQueryParam("keyword"), {
        replace: true,
      });
      if (term.length > 0) {
        KeywordService.save(term);
      }
    }, 400);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordString]);

  // Fire the page-specific suggestion lookups for the current keyword. Runs the
  // queries in parallel (instead of the old sequential awaits) and passes an
  // AbortSignal so a superseded request is cancelled. Bails out before touching
  // state if the request was aborted, so a stale response can't overwrite the
  // suggestions for a newer keystroke.
  const fetchSuggestions = async (string, signal) => {
    const opts = { signal };
    let temp = [];

    if (location.pathname.includes("/work") || location.pathname.includes("/recommendations")) {
      const [woRes, customerRes] = await Promise.all([
        WOService.getAllOrderNumberWithKeyword(string, branch, opts),
        CustomerService.getAll(string, opts),
      ]);
      const woNumbers = woRes.data;
      const customers = customerRes.data.map((item) => item.companyName);
      temp = [...woNumbers, ...customers];
    } else if (location.pathname.includes("/purchase")) {
      const [poRes, vendorRes, woRes, customerRes] = await Promise.all([
        POService.getAllOrderNumberWithKeyword(string, branch, opts),
        VendorService.getAllForSelection(string, opts),
        WOService.getAllOrderNumberWithKeyword(string, branch, opts),
        CustomerService.getAll(string, opts),
      ]);
      const poNumbers = poRes.data;
      const vendors = vendorRes.data.map((item) => item.name);
      const woNumbers = woRes.data;
      const customers = customerRes.data.map((item) => item.companyName);
      temp = [...poNumbers, ...vendors, ...woNumbers, ...customers];
    } else if (location.pathname.includes("/customer")) {
      // Live-filter the customer page as the user types. The keyword param is
      // pushed to the URL by the debounce effect (shared with the other pages).
      const res = await AccountService.getAll({}, opts);
      const term = (string || "").toLowerCase();
      temp = (res.data || [])
        .map((item) => item.name)
        .filter((name) => name && name.toLowerCase().includes(term));
    } else if (location.pathname.includes("/settings/vendor")) {
      const res = await VendorService.getAll(string, opts);
      temp = res.data.map((item) => item.name);
    }

    if (signal.aborted) return;
    setSuggestionArray(temp);
    setIsDropdownOpen(temp.length > 0);
  };

  const keywordChanged = (e) => {
    const string = e.target.value;
    isTypingRef.current = true;
    // Update the input immediately so typing never lags behind the keystroke;
    // only the suggestion network calls are debounced below.
    setKeywordString(string);
    if (string.length === 0) {
      dispatch(setKeyword(""));
    }

    // Debounce the suggestion lookups: cancel any pending timer and any in-flight
    // request, then schedule a fresh batch 300ms after the user stops typing.
    if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
    if (suggestionAbortRef.current) suggestionAbortRef.current.abort();

    suggestionTimerRef.current = setTimeout(() => {
      const controller = new AbortController();
      suggestionAbortRef.current = controller;
      fetchSuggestions(string, controller.signal).catch((error) => {
        // Ignore cancellations from a superseded keystroke; surface real errors.
        if (controller.signal.aborted || error?.name === "CanceledError" || error?.name === "AbortError") {
          return;
        }
        handleError(error);
      });
    }, 300);
  };

  // Clean up any pending debounce/in-flight request on unmount.
  useEffect(() => {
    return () => {
      if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
      if (suggestionAbortRef.current) suggestionAbortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    // setKeywordString("");
    // dispatch(setKeyword(""));
    // removeQueryParam("keyword");
    setFocusedIndex(-1);
  }, [location.pathname]);

  const handleKeyDown = (e) => {
    let string = e.target.value;
    if (e.key === "Enter") {
      // Immediate search on Enter — bypass the debounce so it doesn't fire again.
      isTypingRef.current = false;
      if (focusedIndex >= 0) {
        if (string.length > 0) {
          setKeywordString(suggestionArray[focusedIndex]);
          // dispatch(setKeyword(suggestionArray[focusedIndex]));
          navigate(appendQueryParam("keyword", suggestionArray[focusedIndex]));
          KeywordService.save(suggestionArray[focusedIndex]);
        } else {
          setKeywordString(historyArray[focusedIndex]);
          // dispatch(setKeyword(historyArray[focusedIndex]));
          navigate(appendQueryParam("keyword", historyArray[focusedIndex]));
          KeywordService.save(historyArray[focusedIndex]);
        }
        setIsDropdownOpen(false);
      } else {
        if (string.length > 0) {
          // dispatch(setKeyword(string));
          navigate(appendQueryParam("keyword", string));
          KeywordService.save(string);
        }
      }
    } else if (e.key === "ArrowDown") {
      if (string.length > 0 && focusedIndex < suggestionArray.length - 1) {
        setFocusedIndex(focusedIndex + 1);
        scrollToItem(focusedIndex + 1);
      }
      if (string.length === 0 && focusedIndex < historyArray.length - 1) {
        setFocusedIndex(focusedIndex + 1);
        scrollToItem(focusedIndex + 1);
      }
    } else if (e.key === "ArrowUp") {
      if (focusedIndex > 0) {
        setFocusedIndex(focusedIndex - 1);
        scrollToItem(focusedIndex - 1);
      }
    }
  };

  const handleFocus = async () => {
    let res = await KeywordService.getAll(10);
    let keywords = res.data;
    if (keywords.length > 0) {
      setHistoryArray(keywords);
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  const handleKeywordClick = (keyword) => {
    // Picking a suggestion/history item navigates immediately — skip debounce.
    isTypingRef.current = false;
    setKeywordString(keyword);
    // dispatch(setKeyword(keyword));
    navigate(appendQueryParam("keyword", keyword));
    setIsDropdownOpen(false);
    KeywordService.save(keyword);
  };

  const scrollToItem = (index) => {
    if (itemRefs.current[index]) {
      itemRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  const isWoDetailPage = () => {
    return /^\/work\/\d+/.test(location.pathname);
  };
  const isPoDetailPage = () => {
    return /^\/purchase\/\d+/.test(location.pathname);
  };

  const appendQueryParam = (query, value, queryToRemove) => {
    let basePath = location.pathname;

    // ✅ if we are on /work/:id, force navigation to /work
    if (isWoDetailPage()) {
      basePath = "/work";
    } else if (isPoDetailPage()) {
      basePath = "/purchase";
    }

    const url = new URLSearchParams(location.search);
    url.set(query, value);

    if (queryToRemove) {
      url.delete(queryToRemove);
    }

    return `${basePath}?${url.toString()}`;
  };

  const removeQueryParam = (queryToRemove) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    const url = new URLSearchParams(currentSearch);
    if (queryToRemove) {
      url.delete(queryToRemove);
    }

    return `${currentPath}?${url.toString()}`;
  };

  return (
    <div className="flex items-center gap-2 w-[264px] h-[32px] rounded-lg border-[1px] border-[#C9CEE2] bg-white px-3 relative" data-tour="wo-search">
      <div className="w-[24px] flex items-center justify-center shrink-0">
        <IoIosSearch size={16} color="#68778D" />
      </div>
      <input
        value={keywordString}
        type="text"
        onChange={keywordChanged}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={() => {
          setIsDropdownOpen(false);
          setFocusedIndex(-1);
        }}
        className="w-full bg-transparent text-[14px] leading-[20px] tracking-[0.14px] text-titleColor font-OpenSansRegular placeholder:text-[#868DA6]"
        placeholder="Search"
      />
      {keywordString.length > 0 && (
        <div
          onClick={() => {
            // dispatch(setKeyword(""));
            isTypingRef.current = false;
            navigate(removeQueryParam("keyword"));
            setKeywordString("");
          }}
          className="cursor-pointer p-1 shrink-0"
          data-tour="wo-search-clear"
        >
          <TiDeleteOutline color="gray" size={14} />
        </div>
      )}
      {isDropdownOpen && (
        <ul
          ref={containerRef}
          className="absolute z-10 left-0 top-full mt-1 w-full bg-white border rounded-md shadow-lg max-h-[300px] overflow-y-auto"
        >
          {keywordString.length > 0
            ? suggestionArray.map((suggestion, index) => (
              <li
                ref={(el) => (itemRefs.current[index] = el)}
                key={index}
                onMouseDown={() => handleKeywordClick(suggestion)}
                className={`px-3 py-1 cursor-pointer font-OpenSansRegular text-[12px] flex items-center gap-2 ${index === focusedIndex ? "bg-gray-200" : "hover:bg-gray-200"
                  }`}
              >
                <div>
                  <IoIosSearch size={16} />
                </div>
                {suggestion}
              </li>
            ))
            : historyArray.map((keyword, index) => (
              <li
                ref={(el) => (itemRefs.current[index] = el)}
                key={index}
                onMouseDown={() => handleKeywordClick(keyword)}
                // className="px-3 py-1 cursor-pointer hover:bg-gray-200 font-OpenSansRegular text-[12px] flex items-center gap-2"
                className={`px-3 py-1 cursor-pointer font-OpenSansRegular text-[12px] flex items-center gap-2 ${index === focusedIndex ? "bg-gray-200" : "hover:bg-gray-200"
                  }`}
              >
                <VscHistory size={13} />
                {keyword}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export default SearchBox;
